import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";

import { UpdatedSearchAction } from "../Components/ChatContainer.jsx";
import ChatSearchHistory from "../Components/ChatSearchHistory.jsx";
import Result from "../Components/Result.jsx";
import ResultSummaryPreview from "../Components/ResultSummaryPreview.jsx";
import ToggleDropdown from "../Components/ToggleDropdown.jsx";
import useStore from "../store.js";
import useVisibleTrigger from "../hooks/useVisibleTrigger.js";
import { buildPendingRefinementSituation } from "../utils/refinementDisplay.js";

const RESULT_PREVIEW_FIXTURE = {
  generation_status: "completed",
  data: {
    summary: {
      headline: "내 상황처럼 계약이 끝난 뒤에도 보증금을 돌려받지 못한 판례에서는 임대인의 반환 책임을 인정했습니다.",
      emphasis: "임대인의 반환 책임을 인정했습니다",
      sources: [1, 2, 3].map(caseIndex => ({
        case_index: caseIndex,
        quote: "계약 종료 후 임대인의 보증금 반환 책임을 인정하였다.",
      })),
      supporting_fact: null,
    },
    evidence: [
      {
        name: "선행 차량 운전자의 진술",
        importance: "선행 차량 운전자의 진술이 상당히 구체적이고",
        sources: [],
      },
      {
        name: "임대차계약서",
        importance: "계약 종료일과 보증금 액수",
        sources: [{ case_index: 1, quote: "임대차계약서에 계약 종료일과 보증금이 적혀 있다." }],
      },
    ],
    self_check_questions: [{
      id: "visual-question-1",
      question: "계약이 끝난 날짜를 상대방에게 알렸나요?",
      yes_fact: "계약 종료일을 상대방에게 알림",
      no_fact: "계약 종료일을 상대방에게 알리지 않음",
      reason: "판례에서 계약 종료 통지를 확인했습니다.",
      sources: [{ case_index: 1, quote: "임차인이 임대인에게 계약 종료를 통지하였다." }],
    }],
  },
};

const CHAT_FIXTURE_KEYS = [
  "chatHistory",
  "updatedQuestionHistory",
  "updatedQuestion",
  "hasUpdatedQuestion",
  "questionHistoryViewIndex",
  "isAIResponding",
  "isChatDone",
  "issues",
  "userIssues",
  "aiIssues",
  "situation",
  "textValue",
  "pendingRefinement",
];

const REFINEMENT_BASE_SITUATION = "집합건물 관리비·분담금 승계 관련 상황입니다.";
const REFINEMENT_SERVER_SITUATION = `${REFINEMENT_BASE_SITUATION} 경매로 소유권을 취득하지 않았습니다.`;

const RefinementWaitingPreview = () => {
  const showPendingQuestion = () => {
    const pendingSituation = buildPendingRefinementSituation(
      REFINEMENT_BASE_SITUATION,
      [{ question: "경매로 소유권을 취득했습니까?", answer: "no" }],
    );
    useStore.setState({
      situation: pendingSituation,
      textValue: pendingSituation,
      pendingRefinement: { baseSituation: REFINEMENT_BASE_SITUATION },
    });
  };

  const showServerQuestion = () => {
    useStore.setState({
      situation: REFINEMENT_SERVER_SITUATION,
      textValue: REFINEMENT_SERVER_SITUATION,
    });
  };

  return (
    <RefinementPreview>
      <PreviewActions>
        <PreviewButton type="button" onClick={showPendingQuestion}>
          재검색 직후 보기
        </PreviewButton>
        <PreviewButton type="button" onClick={showServerQuestion}>
          서버 질문 반영 보기
        </PreviewButton>
      </PreviewActions>
      <Result resultId="waiting" onlySituationBox />
    </RefinementPreview>
  );
};

const FeedbackScrollPreview = () => {
  const triggerRef = useRef(null);
  const [hasReachedLastCase, setHasReachedLastCase] = useState(false);

  useVisibleTrigger({
    enabled: true,
    targetRef: triggerRef,
    onVisible: () => {
      setHasReachedLastCase(true);
      return true;
    },
  });

  return (
    <FeedbackPreview>
      {[1, 2, 3, 4, 5].map(index => (
        <FeedbackCase key={index}>유사 판례 {index}</FeedbackCase>
      ))}
      <FeedbackMarker ref={triggerRef} data-testid="visual-feedback-trigger" />
      <FeedbackStatus data-testid="visual-feedback-status">
        {hasReachedLastCase ? "마지막 판례 도달 · 리뷰 요청" : "마지막 판례 도달 전 · 리뷰 대기"}
      </FeedbackStatus>
    </FeedbackPreview>
  );
};

const VisualRegressionPage = () => {
  const [isQuestionCollapsed, setIsQuestionCollapsed] = useState(true);
  const previousChatStateRef = useRef(null);

  useEffect(() => {
    const currentState = useStore.getState();
    previousChatStateRef.current = Object.fromEntries(
      CHAT_FIXTURE_KEYS.map(key => [key, currentState[key]]),
    );

    useStore.setState({
      chatHistory: [],
      updatedQuestionHistory: [{
        content: "임대인이 계약 종료 후에도 보증금을 반환하지 않고 있습니다.",
      }],
      updatedQuestion: "",
      hasUpdatedQuestion: true,
      questionHistoryViewIndex: 0,
      isAIResponding: false,
      isChatDone: true,
      issues: ["보증금 반환", "계약 종료"],
      userIssues: ["계약 종료"],
      aiIssues: ["보증금 반환"],
      situation: "임대차계약이 끝났지만 임대인이 보증금을 반환하지 않고 있습니다.",
      textValue: REFINEMENT_BASE_SITUATION,
      pendingRefinement: null,
    });

    return () => {
      if (previousChatStateRef.current) {
        useStore.setState(previousChatStateRef.current);
      }
    };
  }, []);

  return (
    <Page>
      <Section aria-labelledby="guest-chat-controls-title">
        <Title id="guest-chat-controls-title">비로그인 채팅 UI 회귀 화면</Title>
        <Description>
          이미지 보간 없이 렌더링되는 채팅 액션을 모바일·태블릿·웹에서 확인합니다.
        </Description>
        <Preview>
          <UpdatedSearchAction type="button" />
        </Preview>
      </Section>
      <Section aria-labelledby="mobile-chat-disclosure-title">
        <Title id="mobile-chat-disclosure-title">모바일 질문·쟁점 화살표</Title>
        <Description>
          하단 카드가 접히면 아래, 펼치면 위로 움직이는 다음 동작 방향을 확인합니다.
        </Description>
        <DisclosurePreview>
          <ChatSearchHistory
            isMobileMode
            isCollapsible
            isCollapsed={isQuestionCollapsed}
            onToggleCollapse={() => setIsQuestionCollapsed(value => !value)}
          />
        </DisclosurePreview>
      </Section>
      <Section aria-labelledby="situation-toggle-title">
        <Title id="situation-toggle-title">내 상황 펼쳐보기 기존 크기</Title>
        <Description>
          기존 115×32 버튼과 312px 본문 카드의 크기·간격을 확인합니다.
        </Description>
        <SituationTogglePreview>
          <ToggleDropdown id="visual-result" />
        </SituationTogglePreview>
      </Section>
      <Section aria-labelledby="result-preview-title">
        <Title id="result-preview-title">분석 결과 타이포그래피</Title>
        <Description>
          분석 결론과 증거·추가 질문 제목의 크기 및 반응형 폭을 확인합니다.
        </Description>
        <ResultPreview>
          <ResultSummaryPreview
            resultId="visual-result"
            preview={RESULT_PREVIEW_FIXTURE}
            caseCount={5}
          />
        </ResultPreview>
      </Section>
      <Section aria-labelledby="refinement-waiting-title">
        <Title id="refinement-waiting-title">재검색 대기 내 상황</Title>
        <Description>
          재검색 직후 추가 질문·답변을 표시하고 서버 질문이 도착하면 정식 문장으로 교체합니다.
        </Description>
        <RefinementWaitingPreview />
      </Section>
      <Section aria-labelledby="feedback-trigger-title">
        <Title id="feedback-trigger-title">리뷰 요청 시점</Title>
        <Description>
          다섯 번째 판례의 끝이 화면에 들어올 때만 리뷰 요청 상태로 전환합니다.
        </Description>
        <FeedbackScrollPreview />
      </Section>
    </Page>
  );
};

const Page = styled.main`
  min-height: 100dvh;
  padding: 24px;
  background: #f6f7f6;
  box-sizing: border-box;
`;

const Section = styled.section`
  width: min(100%, 680px);
  margin: 0 auto;
  padding: 24px;
  border-radius: 20px;
  background: #fff;
  box-sizing: border-box;

  & + & {
    margin-top: 24px;
  }
`;

const Title = styled.h1`
  margin: 0;
  font-size: 20px;
  line-height: 28px;
`;

const Description = styled.p`
  margin: 8px 0 24px;
  color: rgba(0, 0, 0, 0.56);
  font-size: 14px;
  line-height: 22px;
`;

const Preview = styled.div`
  display: flex;
  min-height: 80px;
  align-items: center;
  padding: 16px;
  border: 1px dashed rgba(0, 0, 0, 0.14);
  border-radius: 14px;
  box-sizing: border-box;
`;

const DisclosurePreview = styled.div`
  width: 100vw;
  margin-left: 50%;
  display: flex;
  justify-content: center;
  transform: translateX(-50%);
`;

const ResultPreview = styled.div`
  width: min(calc(100vw - 48px), 680px);
  margin-left: 50%;
  transform: translateX(-50%);
`;

const RefinementPreview = styled.div`
  width: min(calc(100vw - 48px), 680px);
  margin-left: 50%;
  transform: translateX(-50%);
`;

const PreviewActions = styled.div`
  margin-bottom: 12px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const PreviewButton = styled.button`
  padding: 8px 12px;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 10px;
  background: #fff;
  color: #333;
  font: inherit;
  cursor: pointer;
`;

const SituationTogglePreview = styled.div`
  position: relative;
  width: 100vw;
  min-height: 360px;
  margin: 40px 0 0 50%;
  transform: translateX(-50%);
`;

const FeedbackPreview = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const FeedbackCase = styled.div`
  min-height: 180px;
  padding: 20px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 16px;
  box-sizing: border-box;
  font-size: 15px;
  font-weight: 600;
`;

const FeedbackMarker = styled.div`
  width: 100%;
  height: 1px;
`;

const FeedbackStatus = styled.div`
  position: sticky;
  bottom: 16px;
  padding: 12px 16px;
  border-radius: 12px;
  background: #111;
  color: #00f370;
  font-size: 14px;
  font-weight: 600;
  text-align: center;
`;

export default VisualRegressionPage;
